import type {
  AgentStatusMetricPayload,
  VoiceChatEventData,
} from "@/types/socket"
import type {
  VoiceSkillResponseConfiguration,
  VoiceToolResult,
} from "@/types/voice-chat"
import type {
  PendingVoiceToolCallState,
  ReconcileRunningVoiceToolCall,
  ToolOutputDelivery,
} from "@/types/voice-chat-rtc"

import { errorMessage } from "@/lib/exception-utils"
import {
  hotelQuoteRefsFromResult,
  toolCandidatesFromResult,
  voiceToolResultTerminal,
} from "@/lib/voice-chat/voice-chat-utils"
import {
  currentTraceEventSeq,
  voiceTracer,
} from "@/lib/voice-trace/voice-trace"

const toolsTrace = voiceTracer("tools")

interface ToolCallRunnerDeps {
  fetchToolResult(args: {
    chatId: number
    openaiCallId: string
    handle: string
  }): Promise<VoiceToolResult>
  onToolProgress(payload: {
    callId: string
    statusMessage?: string
    estimatedCompletionPercent?: number
    complicationMessage?: string
    metricPayload?: AgentStatusMetricPayload
  }): void
  onToolSucceeded(payload: {
    callId: string
    candidates: ReturnType<typeof toolCandidatesFromResult>
    hotelQuoteRefs: string[]
  }): void
  onToolFailed(payload: {
    callId: string
    toolName: string
    message: string
  }): void
  applyResponseConfiguration(
    configuration: VoiceSkillResponseConfiguration
  ): Promise<boolean>
  sendToolOutput(delivery: ToolOutputDelivery): Promise<boolean>
  refreshToolCalls(chatId: number): Promise<unknown>
}

/**
 * Deduplicates terminal delivery per `(callId, handle)` while the backend
 * remains the execution and durable-status authority. Socket events provide
 * progress and wake terminal fetches; persisted DTO reconciliation and fetch
 * completion converge on that same key. Teardown closes write ownership so
 * late async completions cannot mutate a successor session.
 */
export class ToolCallRunner {
  private readonly pendingToolCalls = new Map<
    string,
    PendingVoiceToolCallState
  >()
  private readonly completedToolCallKeys = new Set<string>()
  private readonly inProgressToolCallKeys = new Set<string>()
  private closed = false

  constructor(private deps: ToolCallRunnerDeps) {}

  registerPending({
    chatId,
    openaiCallId,
    customerSpeechGeneration,
    callId,
    toolName,
    handle,
  }: Omit<PendingVoiceToolCallState, "delivered" | "eventSeq">) {
    const existingPending = this.pendingToolCalls.get(callId)
    if (existingPending) return existingPending

    const eventSeq = currentTraceEventSeq()
    const callTrace = voiceTracer(
      "tools",
      { callId, toolName, handle },
      eventSeq
    )
    callTrace.info("voice_tool_wait_started")
    const pending: PendingVoiceToolCallState = {
      chatId,
      openaiCallId,
      customerSpeechGeneration,
      callId,
      toolName,
      handle,
      eventSeq,
      delivered: false,
    }
    this.pendingToolCalls.set(callId, pending)
    return pending
  }

  // Immediate calls and long-running polls converge here. The delivery key uses
  // the Realtime tool-call id plus backend handle so socket wakes and reconnect
  // fetches cannot deliver one terminal result twice.
  async deliver(
    delivery: ToolOutputDelivery,
    handle: string | null,
    trigger: string,
    eventSeq?: number | null
  ) {
    const { callId, toolName, result } = delivery
    if (this.closed || !voiceToolResultTerminal(result)) return

    const deliveryKey = this.deliveredVoiceToolKey(callId, handle)
    if (
      this.completedToolCallKeys.has(deliveryKey) ||
      this.inProgressToolCallKeys.has(deliveryKey)
    ) {
      return
    }
    this.inProgressToolCallKeys.add(deliveryKey)

    const callTrace = voiceTracer(
      "tools",
      { callId, toolName, handle },
      eventSeq
    )
    try {
      if (
        "responseConfiguration" in result &&
        result.responseConfiguration &&
        !(await this.deps.applyResponseConfiguration(
          result.responseConfiguration
        ))
      ) {
        return
      }

      const accepted = await this.deps.sendToolOutput({
        ...delivery,
        additionalToolCalls:
          "additionalToolCalls" in result
            ? result.additionalToolCalls
            : undefined,
      })
      if (!accepted) return

      this.completedToolCallKeys.add(deliveryKey)
      if ("isError" in result && result.isError) {
        this.deps.onToolFailed({
          callId,
          toolName,
          message: result.error ?? "Voice tool call returned an error.",
        })
      } else {
        this.deps.onToolSucceeded({
          callId,
          candidates: toolCandidatesFromResult(result),
          hotelQuoteRefs: hotelQuoteRefsFromResult(result),
        })
      }
      callTrace.info(
        "isError" in result && result.isError
          ? "voice_tool_backend_terminal_error"
          : "voice_tool_backend_terminal_success"
      )
      const pending = this.pendingToolCalls.get(callId)
      if (pending) {
        pending.delivered = true
        this.pendingToolCalls.delete(callId)
      }
      callTrace.info("voice_tool_final_payload_sent", { trigger })
    } finally {
      this.inProgressToolCallKeys.delete(deliveryKey)
    }
  }

  resync(trigger: string) {
    if (this.closed) return

    const pendingCalls = Array.from(this.pendingToolCalls.values())
    if (pendingCalls.length === 0) return

    toolsTrace.info("voice_tool_pending_resync_started", {
      trigger,
      pendingCount: pendingCalls.length,
    })
    pendingCalls.forEach((pending) => {
      void this.fetchPendingVoiceToolResult(pending, trigger)
    })
    new Set(pendingCalls.map((pending) => pending.chatId)).forEach((chatId) => {
      void this.deps.refreshToolCalls(chatId)
    })
  }

  // Socket events are wakeups, not terminal authority: progress updates the UI
  // directly, while completion for a registered pending call fetches the backend
  // record. Reconnect reconciliation recreates pending calls from durable state.
  handleSocketEvent(
    data: Exclude<VoiceChatEventData, { type: "chat_renamed" }>
  ) {
    if (this.closed) return

    const pending =
      this.pendingToolCalls.get(data.call_id) ??
      Array.from(this.pendingToolCalls.values()).find(
        (entry) => entry.handle === data.handle
      )
    if (data.type === "tool_call_progress") {
      this.deps.onToolProgress({
        callId: pending?.callId ?? data.call_id,
        statusMessage: data.status_message,
        estimatedCompletionPercent: data.estimated_completion_percent,
        complicationMessage: data.complication_found,
        metricPayload: data.metric_payload,
      })
      return
    }

    toolsTrace.info(
      pending
        ? "voice_tool_socket_wake_received"
        : "voice_tool_socket_wake_missing_pending",
      {
        callId: data.call_id,
        handle: data.handle,
        eventType: data.type,
      }
    )
    if (pending) {
      void this.fetchPendingVoiceToolResult(pending, "socket")
    }
  }

  reconcile(
    chatId: number,
    openaiCallId: string,
    customerSpeechGeneration: number,
    runningCalls: ReconcileRunningVoiceToolCall[],
    trigger: string
  ) {
    if (this.closed) return

    const calls = runningCalls.filter(
      (call) =>
        call.openaiCallId === openaiCallId &&
        call.status === "running" &&
        call.handle
    )
    if (calls.length === 0) return

    toolsTrace.info("voice_tool_dto_resync_started", {
      trigger,
      pendingCount: calls.length,
    })
    calls.forEach((call) => {
      if (!call.handle) return

      const pending = this.registerPending({
        chatId,
        openaiCallId,
        customerSpeechGeneration,
        callId: call.callId,
        toolName: call.toolName,
        handle: call.handle,
      })
      void this.fetchPendingVoiceToolResult(pending, trigger)
    })
  }

  // Teardown closes the ownership boundary before clearing pending calls. Late
  // fetch completions may still resolve, but `closed` prevents them from writing
  // into a successor voice session.
  teardown() {
    this.closed = true
    this.pendingToolCalls.clear()
  }

  fetchPending(pending: PendingVoiceToolCallState, trigger: string) {
    return this.fetchPendingVoiceToolResult(pending, trigger)
  }

  private async fetchPendingVoiceToolResult(
    pending: PendingVoiceToolCallState,
    trigger: string
  ) {
    if (pending.delivered) return
    const callTrace = voiceTracer(
      "tools",
      {
        callId: pending.callId,
        toolName: pending.toolName,
        handle: pending.handle,
      },
      pending.eventSeq
    )
    callTrace.info("voice_tool_result_fetch_started", { trigger })
    try {
      const result = await this.deps.fetchToolResult({
        chatId: pending.chatId,
        openaiCallId: pending.openaiCallId,
        handle: pending.handle,
      })
      if (this.closed) return

      callTrace.info("voice_tool_result_fetch_completed", {
        trigger,
        terminal: voiceToolResultTerminal(result),
        isError: "isError" in result ? result.isError : undefined,
      })
      await this.deliver(
        {
          callId: pending.callId,
          toolName: pending.toolName,
          customerSpeechGeneration: pending.customerSpeechGeneration,
          result,
        },
        pending.handle,
        trigger,
        pending.eventSeq
      )
    } catch (error) {
      callTrace.warn("voice_tool_result_fetch_failed", {
        trigger,
        message: errorMessage(error, "Could not fetch voice tool result."),
      })
    }
  }

  private deliveredVoiceToolKey(callId: string, handle: string | null) {
    return `${callId}:${handle ?? ""}`
  }
}
